import { useCallback, useEffect, useState } from 'react';
import { httpsCallable, FunctionsError } from 'firebase/functions';
import { functions } from '../lib/firebase';

// ---------- Callable contracts (chunk 2) ----------

export interface ConversionBySourceRow {
  source: string;
  leadsCount: number;
  convertedCount: number;
  conversionPct: number;
}

export interface ConversionBySourceData {
  bySource: ConversionBySourceRow[];
  totalLeads: number;
  totalConverted: number;
}

export interface MultiServiceByCountRow {
  serviceCount: number;
  clientCount: number;
}

export interface MultiServiceClientsData {
  clientsTotal: number;
  clientsMultiService: number;
  multiServicePct: number;
  byServiceCount: MultiServiceByCountRow[];
}

export interface TimeToCloseBucket {
  label: string;
  leadsCount: number;
}

export interface TimeToCloseData {
  buckets: TimeToCloseBucket[];
  medianDays: number;
  p90Days: number;
}

export interface LeadRoiBySourceRow {
  source: string;
  spendCents: number;
  revenueCents: number;
  leadsCount: number;
  dealsCount: number;
  // Basis points (10000 = 1x return).
  roas: number;
  costPerLead: number;
}

export interface LeadRoiData {
  bySource: LeadRoiBySourceRow[];
  totalSpendCents: number;
  totalRevenueCents: number;
  // Basis points (10000 = 1x return).
  totalRoas: number;
}

// ---------- Section state ----------

export type SectionStatus = 'idle' | 'loading' | 'ok' | 'error' | 'access-denied';

export interface SectionState<T> {
  status: SectionStatus;
  data: T | null;
  error: string | null;
}

export interface LeadAttributionExtrasState {
  conversion: SectionState<ConversionBySourceData>;
  multiService: SectionState<MultiServiceClientsData>;
  timeToClose: SectionState<TimeToCloseData>;
  roi: SectionState<LeadRoiData>;
  refresh: () => void;
}

interface Params {
  companyId: string | undefined;
  /** Days window for conversion + ROI calls. Default 90. */
  shortWindowDays?: number;
  /** Days window for multi-service + time-to-close. Default 365. */
  longWindowDays?: number;
}

const initialState = <T,>(): SectionState<T> => ({
  status: 'idle',
  data: null,
  error: null,
});

function classifyError(err: unknown): SectionStatus {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as FunctionsError).code;
    if (code === 'functions/unauthenticated' || code === 'functions/permission-denied') {
      return 'access-denied';
    }
  }
  return 'error';
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Fetches the four PR-3 lead-attribution analytics endpoints in parallel.
 * Each section tracks its own status so a single callable failure doesn't
 * blank out the whole page. Permission/auth errors render as access-denied
 * instead of a generic error.
 */
export function useLeadAttributionExtras(params: Params): LeadAttributionExtrasState {
  const { companyId, shortWindowDays = 90, longWindowDays = 365 } = params;

  const [conversion, setConversion] = useState<SectionState<ConversionBySourceData>>(
    initialState<ConversionBySourceData>()
  );
  const [multiService, setMultiService] = useState<SectionState<MultiServiceClientsData>>(
    initialState<MultiServiceClientsData>()
  );
  const [timeToClose, setTimeToClose] = useState<SectionState<TimeToCloseData>>(
    initialState<TimeToCloseData>()
  );
  const [roi, setRoi] = useState<SectionState<LeadRoiData>>(initialState<LeadRoiData>());

  const [refreshKey, setRefreshKey] = useState<number>(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setConversion(initialState<ConversionBySourceData>());
      setMultiService(initialState<MultiServiceClientsData>());
      setTimeToClose(initialState<TimeToCloseData>());
      setRoi(initialState<LeadRoiData>());
      return;
    }

    const cancelled = { value: false };

    const conversionFn = httpsCallable<
      { companyId: string; days?: number },
      ConversionBySourceData
    >(functions, 'getConversionBySource');
    const multiServiceFn = httpsCallable<
      { companyId: string; days?: number },
      MultiServiceClientsData
    >(functions, 'getMultiServiceClients');
    const timeToCloseFn = httpsCallable<
      { companyId: string; days?: number },
      TimeToCloseData
    >(functions, 'getTimeToClose');
    const roiFn = httpsCallable<{ companyId: string; days?: number }, LeadRoiData>(
      functions,
      'getLeadRoi'
    );

    setConversion({ status: 'loading', data: null, error: null });
    setMultiService({ status: 'loading', data: null, error: null });
    setTimeToClose({ status: 'loading', data: null, error: null });
    setRoi({ status: 'loading', data: null, error: null });

    conversionFn({ companyId, days: shortWindowDays })
      .then((r) => {
        if (cancelled.value) return;
        setConversion({ status: 'ok', data: r.data, error: null });
      })
      .catch((err) => {
        if (cancelled.value) return;
        console.error('[useLeadAttributionExtras] getConversionBySource failed', err);
        setConversion({
          status: classifyError(err),
          data: null,
          error: errorMessage(err),
        });
      });

    multiServiceFn({ companyId, days: longWindowDays })
      .then((r) => {
        if (cancelled.value) return;
        setMultiService({ status: 'ok', data: r.data, error: null });
      })
      .catch((err) => {
        if (cancelled.value) return;
        console.error('[useLeadAttributionExtras] getMultiServiceClients failed', err);
        setMultiService({
          status: classifyError(err),
          data: null,
          error: errorMessage(err),
        });
      });

    timeToCloseFn({ companyId, days: longWindowDays })
      .then((r) => {
        if (cancelled.value) return;
        setTimeToClose({ status: 'ok', data: r.data, error: null });
      })
      .catch((err) => {
        if (cancelled.value) return;
        console.error('[useLeadAttributionExtras] getTimeToClose failed', err);
        setTimeToClose({
          status: classifyError(err),
          data: null,
          error: errorMessage(err),
        });
      });

    roiFn({ companyId, days: shortWindowDays })
      .then((r) => {
        if (cancelled.value) return;
        setRoi({ status: 'ok', data: r.data, error: null });
      })
      .catch((err) => {
        if (cancelled.value) return;
        console.error('[useLeadAttributionExtras] getLeadRoi failed', err);
        setRoi({ status: classifyError(err), data: null, error: errorMessage(err) });
      });

    return () => {
      cancelled.value = true;
    };
  }, [companyId, shortWindowDays, longWindowDays, refreshKey]);

  return { conversion, multiService, timeToClose, roi, refresh };
}
